/** Stable reference_image_inputs + prompt_asset_bindings for Canvas image gen. */

import type { TFunction } from 'i18next'
import type { ImageInputMaterial } from './dataflow'
import { parsePromptText } from './assetMention'

export interface ReferenceImageInput {
  asset_id: string
  asset_ref: string
  label: string
  index: number
  role_optional?: string | null
}

export interface PromptAssetBinding {
  token: string
  asset_ref: string
}

export interface ReferenceBindingState {
  referenceImageInputs: ReferenceImageInput[]
  promptAssetBindings: PromptAssetBinding[]
}

function newAssetRef(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID()
  return `ref-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

export function readReferenceBindingsFromFields(
  fields: Record<string, unknown>,
): { assetRefByEdgeId: Map<string, string>; bindings: ReferenceBindingState } {
  const assetRefByEdgeId = new Map<string, string>()
  const stored = Array.isArray(fields.referenceAssetRefByEdgeId)
    ? (fields.referenceAssetRefByEdgeId as Array<{ edgeId: string; assetRef: string }>)
    : []
  for (const row of stored) {
    if (row?.edgeId && row?.assetRef) assetRefByEdgeId.set(row.edgeId, row.assetRef)
  }
  const referenceImageInputs = Array.isArray(fields.referenceImageInputs)
    ? (fields.referenceImageInputs as ReferenceImageInput[])
    : []
  const promptAssetBindings = Array.isArray(fields.promptAssetBindings)
    ? (fields.promptAssetBindings as PromptAssetBinding[])
    : []
  return {
    assetRefByEdgeId,
    bindings: { referenceImageInputs, promptAssetBindings },
  }
}

export function serializeReferenceAssetRefByEdgeId(assetRefByEdgeId: Map<string, string>) {
  return Array.from(assetRefByEdgeId.entries()).map(([edgeId, assetRef]) => ({ edgeId, assetRef }))
}

export function ensureAssetRefForEdge(
  assetRefByEdgeId: Map<string, string>,
  edgeId: string,
): string {
  const existing = assetRefByEdgeId.get(edgeId)
  if (existing) return existing
  const created = newAssetRef()
  assetRefByEdgeId.set(edgeId, created)
  return created
}

export function pruneAssetRefForEdge(assetRefByEdgeId: Map<string, string>, edgeId: string) {
  assetRefByEdgeId.delete(edgeId)
}

/** Canonical order: connection index on material (stable once assigned). */
export function buildReferenceBindings(args: {
  imageMaterials: ImageInputMaterial[]
  assetRefByEdgeId: Map<string, string>
  prompt: string
  aliasByEdgeId: Map<string, string>
  t: TFunction<'canvas'>
}): ReferenceBindingState {
  const sorted = [...args.imageMaterials]
  const referenceImageInputs: ReferenceImageInput[] = []
  const promptAssetBindings: PromptAssetBinding[] = []
  const assetRefByLabel = new Map<string, string>()

  sorted.forEach((material, idx) => {
    const index = idx + 1
    const label = args.aliasByEdgeId.get(material.edgeId) || `${args.t('dataflow.aliasImage')}${index}`
    const asset_ref = ensureAssetRefForEdge(args.assetRefByEdgeId, material.edgeId)
    if (assetRefByLabel.has(label)) {
      throw new Error(`duplicate reference label: ${label}`)
    }
    assetRefByLabel.set(label, asset_ref)
    referenceImageInputs.push({
      asset_id: material.entityId || '',
      asset_ref,
      label,
      index,
      role_optional: 'reference_image',
    })
  })

  for (const seg of parsePromptText(args.prompt)) {
    if (seg.type !== 'mention') continue
    const alias = args.aliasByEdgeId.get(seg.edgeId)
    if (!alias) continue
    const asset_ref = ensureAssetRefForEdge(args.assetRefByEdgeId, seg.edgeId)
    if (!promptAssetBindings.some((b) => b.token === alias)) {
      promptAssetBindings.push({ token: alias, asset_ref })
    }
  }

  return { referenceImageInputs, promptAssetBindings }
}

export function inferImageGenerationOperation(
  referenceCount: number,
  providerChannel: string | undefined,
): 'CREATE_NEW' | 'IMAGE_TO_IMAGE' | 'CREATE_WITH_REFERENCE' {
  if (referenceCount <= 0) return 'CREATE_NEW'
  if (referenceCount === 1 && providerChannel === 'LOCAL_FLUX') return 'IMAGE_TO_IMAGE'
  return 'CREATE_WITH_REFERENCE'
}
